const PREVIEW_MAX_LENGTH = 50;

/** Lowercase named entities so browsers decode (&Nbsp; → &nbsp;). */
const normalizeHtmlEntities = (raw: string) =>
  raw.replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (_match, entity: string) =>
    entity.startsWith("#") ? _match : `&${entity.toLowerCase()};`
  );

/** Strip HTML to a single line for table previews. */
export const htmlToPlainText = (raw: string): string => {
  const normalized = normalizeHtmlEntities(raw);
  const doc = new DOMParser().parseFromString(normalized, "text/html");
  return (doc.body.textContent || normalized).replace(/\s+/g, " ").trim();
};

/** Strip HTML to plain text while preserving meaningful line breaks for copy/paste. */
export const htmlToCopyableText = (raw: string): string => {
  const normalized = normalizeHtmlEntities(raw);
  const withBreaks = normalized
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*/gi, "\n");

  const doc = new DOMParser().parseFromString(withBreaks, "text/html");
  return (doc.body.textContent || withBreaks)
    .replace(/\r\n/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{2,}/g, "\n\n")
    .trim();
};

export const formatCommunicationPreview = (
  raw: string | null | undefined,
  maxLength = PREVIEW_MAX_LENGTH
): string => {
  if (!raw?.trim()) return "--";

  const plain = htmlToPlainText(raw);
  if (!plain) return "--";
  if (plain.length <= maxLength) return plain;

  return `${plain.slice(0, maxLength).trimEnd()}...`;
};

/**
 * Copy HTML so paste targets (Word, Gmail, Docs, etc.) receive rich formatting.
 * Falls back to selecting a temporary DOM node when ClipboardItem is unavailable.
 */
export const copyHtmlFormatted = async (html: string): Promise<void> => {
  const plain = htmlToCopyableText(html);

  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      // Fall through to DOM selection fallback.
    }
  }

  const container = document.createElement("div");
  container.setAttribute("contenteditable", "true");
  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.style.top = "0";
  container.innerHTML = html;
  document.body.appendChild(container);

  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(container);
  selection?.removeAllRanges();
  selection?.addRange(range);

  const copied = document.execCommand("copy");
  selection?.removeAllRanges();
  document.body.removeChild(container);

  if (!copied) {
    throw new Error("Failed to copy HTML");
  }
};
